import type { FC } from "react";

import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CustomColorPicker from "../../NotificationEditComponents/CustomColorPicker";
import CustomSwitch from "../../CustomSwitch";
import CustomButton from "../../CustomButton";
import { SignatureIconSelect } from "../../SignatureIconSelect";
import { NotificationDefaultSettings } from "../../../types/notification-default-settings";

import { useTheme } from "@mui/material";
import { useSettingsContext } from "../../../context/SettingsContext";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_CUSTOM_LOGO_TEXT } from "./constants";

// Maximum file size: 100 KB (same as server limit)
const MAX_FILE_SIZE = 100 * 1024; // 100 KB in bytes

// Calculate the size of base64 encoded data
const getBase64Size = (base64String?: string): number => {
  if (!base64String) return 0;

  // Remove data URL prefix if present (data:image/png;base64,...)
  const base64 = base64String.split(',')[1] || base64String;

  // Calculate the size of the decoded data
  const padding = (base64.match(/=/g) || []).length;
  const size = (base64.length * 3) / 4 - padding;

  return size;
};

export const CustomLogoTab: FC = () => {
  const { t } = useTranslation();

  const theme = useTheme();
  const {
    webLogo: { logoDisplay, logo: defaultLogo, logoBackgroundColor, logoText },
    handleUpdateLogo,
  } = useSettingsContext();

  const [logo, setLogo] = useState<string | undefined>(defaultLogo);
  const [showLogo, setShowLogo] = useState<boolean>(logoDisplay);
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>(
    logoBackgroundColor,
  );
  const [text, setText] = useState<string>(
    logoText ?? DEFAULT_CUSTOM_LOGO_TEXT,
  );
  const [isLogoValid, setIsLogoValid] = useState<boolean>(true);

  const isLogoTooLarge = useMemo(() => {
    return logo ? getBase64Size(logo) > MAX_FILE_SIZE : false;
  }, [logo]);

  console.log("isLogoTooLarge", isLogoTooLarge);
  console.log("isLogoValid", isLogoValid);

  return (
    <Stack gap={1.5} sx={{ px: 3, py: 2, maxWidth: 300 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          fontSize={18}
          color={theme.palette.main.primary.o}
          fontWeight='bold'
          width={1}
        >
          {t("customLogo.showLogo")}
        </Typography>
        <CustomSwitch
          checked={showLogo}
          onSwitch={() => setShowLogo(prev => !prev)}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          fontSize={18}
          color={theme.palette.main.primary.o}
          fontWeight='bold'
          width={1}
        >
          {t("customLogo.logo")}
        </Typography>
        <SignatureIconSelect
          logo={logo}
          setLogo={setLogo}
          onValidationChange={setIsLogoValid}
        />
      </Box>
      <TextField
        fullWidth
        sx={{ width: 1 }}
        type='text'
        value={text}
        onChange={e => setText(e.currentTarget.value)}
        variant='outlined'
        InputProps={{
          inputProps: { min: 0, style: { height: 10 } },
          endAdornment: (
            <InputAdornment position='end'>
              <Typography
                style={{ color: theme.palette.natural.t1, fontSize: 14 }}
              >
                {t("customLogo.text")}
              </Typography>
            </InputAdornment>
          ),
        }}
      />
      <Stack direction='row' alignItems='center'>
        <Typography
          fontSize={18}
          color={theme.palette.main.primary.o}
          fontWeight='bold'
          width={300}
        >
          {t("customLogo.textColor")}
        </Typography>

        <CustomColorPicker
          color={backgroundColor ?? NotificationDefaultSettings.IconColor}
          resetButton={false}
          onChange={color =>
            setBackgroundColor(color ?? NotificationDefaultSettings.IconColor)
          }
        />
      </Stack>
      <CustomButton
        title={t("customLogo.save")}
        isDisabled={!isLogoValid || isLogoTooLarge}
        onClick={() =>
          handleUpdateLogo({
            logoDisplay: showLogo,
            logo,
            logoBackgroundColor: backgroundColor,
            logoText: text,
          })
        }
      />
    </Stack>
  );
};
